/**
 * Customer Details Card Component
 * Editable customer information form for quotations
 */
import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { User, Phone, MapPin, FileText, Building, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../../lib/store';
import { cn } from '../../lib/utils';
import { logger } from '../../lib/logger';

export function CustomerDetailsCard({ 
  customer, 
  setCustomer, 
  subject, 
  setSubject,
  collapsed = false,
  onToggleCollapse 
}) {
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(collapsed);

  // Search customers
  useEffect(() => {
    if (customerSearch.length < 2) {
      setCustomerSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await api.searchCustomers(customerSearch);
        setCustomerSuggestions(res.data || []);
        setShowSuggestions(true);
      } catch (err) {
        logger.error('Customer search error:', err);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [customerSearch]);

  const selectCustomer = (c) => {
    setCustomer({
      name: c.name || '',
      mobile: c.mobile || '',
      address: c.address || '',
      gst_no: c.gst_no || '',
      project_name: c.project_name || '',
      delivery_location: c.delivery_location || ''
    });
    setCustomerSearch('');
    setShowSuggestions(false);
  };

  const handleToggle = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    onToggleCollapse?.(newState);
  };

  return (
    <Card className="rounded-2xl bg-white shadow-none border-heda-line" data-testid="customer-details-card">
      <CardHeader 
        className="py-3 cursor-pointer hover:bg-heda-cream transition-colors"
        onClick={handleToggle}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-heda-olive-dark flex items-center gap-2">
            <User className="w-4 h-4" />
            Customer Details
          </CardTitle>
          {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </div>
      </CardHeader>
      
      {!isCollapsed && (
        <CardContent className="space-y-4">
          {/* Customer Search */}
          <div className="relative">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-heda-mute" />
                <Input
                  placeholder="Search existing customer..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  className="rounded-xl bg-heda-cream/50 pl-9 text-sm"
                />
              </div>
            </div>
            
            {showSuggestions && customerSuggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-heda-line rounded-xl shadow-lg max-h-48 overflow-y-auto">
                {customerSuggestions.map((c, idx) => (
                  <button
                    key={c.id || idx}
                    onClick={() => selectCustomer(c)}
                    className="w-full px-4 py-2 text-left hover:bg-heda-cream/60 border-b last:border-0 text-sm"
                  >
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-heda-mute">{c.mobile}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Subject */}
          <div>
            <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Quotation Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g., QUOTATION FOR KITCHEN PROJECT"
              className="rounded-xl bg-heda-cream/50 mt-1 text-sm uppercase"
            />
          </div>

          {/* Customer Name & Mobile */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute flex items-center gap-1">
                <User className="w-3 h-3" /> Name *
              </Label>
              <Input
                value={customer.name}
                onChange={(e) => setCustomer(c => ({ ...c, name: e.target.value }))}
                placeholder="Customer name"
                className="rounded-xl bg-heda-cream/50 mt-1 text-sm"
                data-testid="customer-name-input"
              />
            </div>
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute flex items-center gap-1">
                <Phone className="w-3 h-3" /> Mobile
              </Label>
              <Input
                value={customer.mobile}
                onChange={(e) => setCustomer(c => ({ ...c, mobile: e.target.value }))}
                placeholder="Mobile number"
                className="rounded-xl bg-heda-cream/50 mt-1 text-sm"
                data-testid="customer-mobile-input"
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Address
            </Label>
            <Input
              value={customer.address}
              onChange={(e) => setCustomer(c => ({ ...c, address: e.target.value }))}
              placeholder="Full address"
              className="rounded-xl bg-heda-cream/50 mt-1 text-sm"
            />
          </div>

          {/* GST & Project */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute flex items-center gap-1">
                <FileText className="w-3 h-3" /> GST No.
              </Label>
              <Input
                value={customer.gst_no}
                onChange={(e) => setCustomer(c => ({ ...c, gst_no: e.target.value }))}
                placeholder="GST number"
                className="rounded-xl bg-heda-cream/50 mt-1 text-sm uppercase"
              />
            </div>
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute flex items-center gap-1">
                <Building className="w-3 h-3" /> Project
              </Label>
              <Input
                value={customer.project_name}
                onChange={(e) => setCustomer(c => ({ ...c, project_name: e.target.value }))}
                placeholder="Project name"
                className="rounded-xl bg-heda-cream/50 mt-1 text-sm"
              />
            </div>
          </div>

          {/* Delivery Location */}
          <div>
            <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Delivery Location
            </Label>
            <Input
              value={customer.delivery_location}
              onChange={(e) => setCustomer(c => ({ ...c, delivery_location: e.target.value }))}
              placeholder="Delivery location"
              className="rounded-xl bg-heda-cream/50 mt-1 text-sm"
            />
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export default CustomerDetailsCard;
